import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import { Text, PopoverMenu, TextButton } from '@wix/design-system';
import { SortingMethod } from '@wix/ambassador-explore-catalog/types';
import { useTranslation } from '@wix/wix-i18n-config';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import {
  ACTIONS,
  useExploreContext,
} from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams } from '@utils/route';
import { useBi } from '@components/Hooks/useBi';
import { useShallowRouter } from '@components/Hooks/useRouter';
import s from './SortMenu.module.scss';

const MobileSortBy = ({ children, options, value, onChange, biData }: any) => {
  const logger = useBi();
  return (
    <div className={s.container} data-hook="filter-select-container">
      <select
        data-hook="filter-select"
        className={s.selectOverlay}
        id="filter-select"
        value={value}
        onChange={onChange}
        onClick={() => {
          const { sessionId, origin, entry, currentPage } = biData;
          logger.report(
            exploreResultsPageGenericClick({
              clickType: 'sort by drop down',
              sessionId,
              origin,
              entry,
              exploreVersion: 'V2',
              data: JSON.stringify({ currentPage }),
            }),
          );
        }}
      >
        {options.map((option: any) => (
          <option
            key={option.id}
            value={option.id}
            selected={option.id === value ? true : false}
          >
            {option.value}
          </option>
        ))}
      </select>
      <label className={s.label} htmlFor="filter-select">
        {children}
      </label>
    </div>
  );
};

const SortMenu: FC = () => {
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const {
    dispatch,
    state: {
      selectedSortingMethod,
      currentPage,
      entry,
      origin,
      isMobileUserAgent,
    },
  } = useExploreContext();

  const isMobileView = useMobileMediaQuery(isMobileUserAgent);

  const options = useMemo(
    () => [
      { id: SortingMethod.RELEVANCE, value: t('relevance') },
      { id: SortingMethod.PRICE_LOW_TO_HIGH, value: t('price_low_to_high') },
      { id: SortingMethod.PRICE_HIGH_TO_LOW, value: t('price_high_to_low') },
    ],
    [t],
  );

  const sortingMenuOnClick = (sortingMethod: SortingMethod) => {
    biGenericClickEvents('sort choice', sortingMethod);
    navigate(
      updateQueryParams(
        {
          sort: sortingMethod,
        },
        router,
      ),
    );

    dispatch({
      type: ACTIONS.SET_SORTING_METHOD,
      sortingMethod,
    });
  };

  if (isMobileView) {
    return (
      <form>
        <MobileSortBy
          options={options}
          value={selectedSortingMethod}
          onChange={(e: any) => {
            switch (e.target.value) {
              default:
              case SortingMethod.RELEVANCE:
                sortingMenuOnClick(SortingMethod.RELEVANCE);
                break;
              case SortingMethod.PRICE_LOW_TO_HIGH:
                sortingMenuOnClick(SortingMethod.PRICE_LOW_TO_HIGH);
                break;
              case SortingMethod.PRICE_HIGH_TO_LOW:
                sortingMenuOnClick(SortingMethod.PRICE_HIGH_TO_LOW);
                break;
            }
          }}
          biData={{ currentPage, entry, origin, sessionId }}
        >
          <TextButton skin="dark" size="medium" suffixIcon={<ChevronDown />}>
            {t('sort-by')}
          </TextButton>
        </MobileSortBy>
      </form>
    );
  }

  function biGenericClickEvents(clickType: string, sortType?: string) {
    logger.report(
      exploreResultsPageGenericClick({
        clickType,
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data:
          clickType === 'sort by drop down'
            ? JSON.stringify({ currentPage })
            : JSON.stringify({ currentPage, sortType }),
      }),
    );
  }

  return (
    <PopoverMenu
      dataHook="blackpopover-sort-menu"
      fixed={false}
      triggerElement={
        <TextButton
          skin="dark"
          size="medium"
          suffixIcon={<ChevronDown />}
          dataHook="black-sort-popover-trigger"
        >
          {t('sort-by')}&nbsp;
          <Text size="medium" dataHook="blackpopover-sort-menu-title">
            {t(_.toLower(selectedSortingMethod || SortingMethod.RELEVANCE))}
          </Text>
        </TextButton>
      }
      onShow={() => {
        biGenericClickEvents('sort by drop down');
      }}
    >
      {_.map(options, (option) => (
        <PopoverMenu.MenuItem
          key={option.id}
          dataHook={`sort-menu-item-${option.value}`}
          onClick={() => {
            sortingMenuOnClick(option.id);
          }}
          text={option.value}
        />
      ))}
    </PopoverMenu>
  );
};

export default SortMenu;
